import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import "leaflet/dist/leaflet.css";
import { cn } from "@/lib/utils";
import { Crosshair, MapPin } from "lucide-react";
import { PinContextMenu, type PinMenuState } from "./pin-context-menu";
import { BASEMAPS, BasemapSwitcher, type BasemapId } from "./basemap-switcher";

export type MapPoint = { lat: number; lng: number; label?: string; vehicleType?: string };

const VEHICLE_ICON_PATHS: Record<string, string> = {
  // circle(s) = wheels, path = frame/body — simple silhouettes, not brand assets
  bike: '<circle cx="6" cy="17" r="3"/><circle cx="18" cy="17" r="3"/><path d="M6 17l4-9h4l3 5"/><path d="M10 8h3"/><path d="M14 13h5"/>',
  car: '<path d="M4 16V11l2-4h12l2 4v5"/><path d="M4 16h16"/><circle cx="7.5" cy="17.5" r="1.7"/><circle cx="16.5" cy="17.5" r="1.7"/>',
  bus: '<rect x="4" y="6" width="16" height="11" rx="1.5"/><path d="M4 12h16"/><path d="M7 9h.01M12 9h.01M17 9h.01"/><circle cx="7.5" cy="19" r="1.4"/><circle cx="16.5" cy="19" r="1.4"/>',
  truck: '<path d="M3 8h10v8H3z"/><path d="M13 11h4l3 3v2h-7z"/><circle cx="6.5" cy="18" r="1.6"/><circle cx="16.5" cy="18" r="1.6"/>',
};

function vehicleIconPath(vehicleType?: string) {
  const n = (vehicleType ?? "").toLowerCase();
  if (n.includes("bike") || n.includes("moto") || n.includes("cycle")) return VEHICLE_ICON_PATHS.bike;
  if (n.includes("car")) return VEHICLE_ICON_PATHS.car;
  if (n.includes("bus") || n.includes("van")) return VEHICLE_ICON_PATHS.bus;
  if (n.includes("truck")) return VEHICLE_ICON_PATHS.truck;
  return VEHICLE_ICON_PATHS.car;
}
export type RoutePing = { lat: number; lng: number; recorded_at: string };

const STOP_RADIUS_M = 70;
const STOP_MINUTES = 30;

function haversineM(a: { lat: number; lng: number }, b: { lat: number; lng: number }) {
  const R = 6371000;
  const dLat = ((b.lat - a.lat) * Math.PI) / 180;
  const dLng = ((b.lng - a.lng) * Math.PI) / 180;
  const s =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((a.lat * Math.PI) / 180) * Math.cos((b.lat * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(s));
}

/** Classifies each segment (the line between ping k and k+1) as "moving" or
 * "stopped", one entry per segment (length pings.length-1). Two different
 * triggers, handled differently:
 *
 * - A same-spot dwell (consecutive pings within STOP_RADIUS_M of each other,
 *   spanning STOP_MINUTES+) is ONE continuous physical stop — every segment
 *   inside it renders "stopped" with no flicker, and it only reverts once,
 *   on the segment right after the dwell ends.
 * - A plain time gap of STOP_MINUTES+ between two consecutive pings (device
 *   went quiet, reappeared somewhere else) toggles the current state instead
 *   of forcing "stopped" — so back-to-back gaps (sparse pinging while still
 *   actually moving) alternate blue/purple/blue rather than merging into one
 *   long purple stretch. Normal-cadence segments always force a reset back
 *   to "moving", so an isolated single gap still auto-reverts right after.
 */
function dwellSegments(pings: RoutePing[]): boolean[] {
  const inDwell = new Array(Math.max(pings.length - 1, 0)).fill(false);
  let i = 0;
  while (i < pings.length) {
    let j = i;
    while (j + 1 < pings.length && haversineM(pings[i], pings[j + 1]) <= STOP_RADIUS_M) j++;
    const spanMin = (new Date(pings[j].recorded_at).getTime() - new Date(pings[i].recorded_at).getTime()) / 60000;
    if (spanMin >= STOP_MINUTES) {
      for (let k = i; k < j; k++) inDwell[k] = true;
    }
    i = j > i ? j + 1 : i + 1;
  }
  return inDwell;
}

/** Classifies each segment (the line between ping k and k+1) as "moving" or
 * "stopped", one entry per segment (length pings.length-1). Two different
 * triggers, handled differently:
 *
 * - A same-spot dwell (consecutive pings within STOP_RADIUS_M of each other,
 *   spanning STOP_MINUTES+) is ONE continuous physical stop — every segment
 *   inside it renders "stopped" with no flicker, and it only reverts once,
 *   on the segment right after the dwell ends.
 * - A plain time gap of STOP_MINUTES+ between two consecutive pings (device
 *   went quiet, reappeared somewhere else) toggles the current state instead
 *   of forcing "stopped" — so back-to-back gaps (sparse pinging while still
 *   actually moving) alternate blue/purple/blue rather than merging into one
 *   long purple stretch. Normal-cadence segments always force a reset back
 *   to "moving", so an isolated single gap still auto-reverts right after.
 */
export function classifySegments(pings: RoutePing[]): ("moving" | "stopped")[] {
  if (pings.length < 2) return [];
  const inDwell = dwellSegments(pings);

  const segments: ("moving" | "stopped")[] = [];
  let current: "moving" | "stopped" = "moving";
  for (let k = 0; k < pings.length - 1; k++) {
    const gapMin = (new Date(pings[k + 1].recorded_at).getTime() - new Date(pings[k].recorded_at).getTime()) / 60000;
    if (inDwell[k]) {
      current = "stopped";
    } else if (gapMin >= STOP_MINUTES) {
      current = current === "moving" ? "stopped" : "moving";
    } else {
      current = "moving";
    }
    segments.push(current);
  }
  return segments;
}

/** Per-ping "stopped" flag for table/list display, kept in lockstep with the
 * map's toggled classifySegments — a ping's status is the segment leaving it
 * (or, for the last ping, the segment arriving at it), so the table always
 * reads Moving/Stopped exactly where the map reads blue/purple. */
export function markStops(pings: RoutePing[]): boolean[] {
  if (pings.length === 0) return [];
  const segments = classifySegments(pings);
  if (segments.length === 0) return [false];
  return pings.map((_, idx) => (idx < segments.length ? segments[idx] : segments[segments.length - 1]) === "stopped");
}

/**
 * OpenStreetMap-backed live map for a parcel.
 * Loads Leaflet on the client only (SSR-safe).
 *
 * The backend currently only exposes a live `current` position (via
 * parcelsApi.location) and no `origin`/`destination` coordinates — only
 * address strings. All three points are therefore optional; when none are
 * available this renders a graceful "no location data" placeholder instead
 * of fabricating coordinates.
 */
export type LiveMapHandle = {
  /** Drop a pin for a searched place and pan (not fit/zoom-reset) to it. */
  goToSearchResult: (lat: number, lng: number, label?: string) => void;
};

export const LiveMap = forwardRef<LiveMapHandle, {
  origin?: MapPoint | null;
  destination?: MapPoint | null;
  current?: MapPoint | null;
  /** Same-day ping trail — rendered as a route line, colored per-segment:
   * blue while moving, amber where the vehicle stayed put 30+ minutes. */
  history?: RoutePing[] | null;
  className?: string;
}>(function LiveMap({
  origin,
  destination,
  current,
  history,
  className,
}, ref) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<any>(null);
  const leafletRef = useRef<any>(null);
  const layerGroupRef = useRef<any>(null);
  const currentMarkerRef = useRef<any>(null);
  const searchMarkerRef = useRef<any>(null);
  const tileLayerRef = useRef<any>(null);
  const labelsLayerRefs = useRef<any[]>([]);
  const hasFitRef = useRef(false);
  const [mapReady, setMapReady] = useState(false);
  const [offScreen, setOffScreen] = useState(false);
  const [basemap, setBasemap] = useState<BasemapId>("osm");
  const [pinMenu, setPinMenu] = useState<PinMenuState>(null);
  const points = [origin, destination, current].filter(
    (p): p is MapPoint => !!p,
  );
  const hasHistory = !!history && history.length > 0;
  const hasMap = points.length > 0 || hasHistory;

  const checkOffScreen = () => {
    const map = mapRef.current;
    const marker = currentMarkerRef.current;
    if (!map || !marker) {
      setOffScreen(false);
      return;
    }
    setOffScreen(!map.getBounds().contains(marker.getLatLng()));
  };

  const goToCurrent = () => {
    const map = mapRef.current;
    const marker = currentMarkerRef.current;
    if (!map || !marker) return;
    map.panTo(marker.getLatLng());
    setOffScreen(false);
  };

  useImperativeHandle(ref, () => ({
    goToSearchResult(lat: number, lng: number, label?: string) {
      const map = mapRef.current;
      const L = leafletRef.current;
      if (!map || !L) return;
      if (searchMarkerRef.current) map.removeLayer(searchMarkerRef.current);
      const icon = L.divIcon({
        className: "",
        html: '<div style="width:16px;height:16px;border-radius:9999px;background:#f97316;box-shadow:0 0 0 4px rgba(249,115,22,0.25);border:2px solid white"></div>',
        iconSize: [16, 16],
        iconAnchor: [8, 8],
      });
      const marker = L.marker([lat, lng], { icon, zIndexOffset: 900 })
        .addTo(map)
        .bindTooltip(label ?? "Searched location")
        .openTooltip();
      marker.on("contextmenu", (e: any) => {
        L.DomEvent.preventDefault(e);
        setPinMenu({ x: e.originalEvent.clientX, y: e.originalEvent.clientY, lat, lng, label });
      });
      searchMarkerRef.current = marker;
      map.panTo([lat, lng]);
    },
  }), []);

  const removeSearchPin = () => {
    const map = mapRef.current;
    if (searchMarkerRef.current && map) map.removeLayer(searchMarkerRef.current);
    searchMarkerRef.current = null;
  };

  // Map creation — runs exactly once per mount (first time there's data to
  // show it for). Does NOT re-run when current/history change, so an
  // incoming ping never tears down and rebuilds the map (which used to reset
  // zoom/pan on every update).
  useEffect(() => {
    if (!hasMap || !containerRef.current || mapRef.current) return;
    let cancelled = false;

    (async () => {
      const L = (await import("leaflet")).default;
      if (cancelled || !containerRef.current) return;
      leafletRef.current = L;

      // Fix default marker icons resolving from bundler
      // @ts-expect-error - internal
      delete L.Icon.Default.prototype._getIconUrl;
      L.Icon.Default.mergeOptions({
        iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
        iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
        shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
      });

      const map = L.map(containerRef.current, { zoomControl: true, attributionControl: true });
      mapRef.current = map;
      tileLayerRef.current = L.tileLayer(BASEMAPS[basemap].url, {
        maxZoom: BASEMAPS[basemap].maxZoom,
        attribution: BASEMAPS[basemap].attribution,
      }).addTo(map);
      labelsLayerRefs.current = (BASEMAPS[basemap].labelsUrls ?? []).map((url) =>
        L.tileLayer(url, { maxZoom: BASEMAPS[basemap].maxZoom }).addTo(map),
      );
      layerGroupRef.current = L.layerGroup().addTo(map);

      // User panned/zoomed away (or we just auto-panned back) — recheck
      // whether the live marker is still in view.
      map.on("moveend", checkOffScreen);
      map.on("movestart zoomstart", () => setPinMenu(null));

      setMapReady(true);
    })();

    return () => {
      cancelled = true;
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
      layerGroupRef.current = null;
      currentMarkerRef.current = null;
      searchMarkerRef.current = null;
      labelsLayerRefs.current = [];
      hasFitRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasMap]);

  // Swap tile layer (and its labels overlays, if any) in place when the user
  // picks a different basemap — never rebuilds the map, so zoom/pan/markers
  // stay untouched.
  useEffect(() => {
    const map = mapRef.current;
    const L = leafletRef.current;
    if (!mapReady || !map || !L) return;
    if (tileLayerRef.current) map.removeLayer(tileLayerRef.current);
    labelsLayerRefs.current.forEach((layer) => map.removeLayer(layer));
    tileLayerRef.current = L.tileLayer(BASEMAPS[basemap].url, {
      maxZoom: BASEMAPS[basemap].maxZoom,
      attribution: BASEMAPS[basemap].attribution,
    }).addTo(map);
    tileLayerRef.current.bringToBack();
    // Tile panes always render below Leaflet's marker/overlay panes, so
    // these sit under existing markers/polylines without extra z-ordering.
    labelsLayerRefs.current = (BASEMAPS[basemap].labelsUrls ?? []).map((url) =>
      L.tileLayer(url, { maxZoom: BASEMAPS[basemap].maxZoom }).addTo(map),
    );
  }, [basemap, mapReady]);

  // Redraw — runs on every data change, but only ever mutates markers/lines
  // inside the layer group plus the current-position marker. Never touches
  // map center/zoom except for the one-time initial fitBounds.
  useEffect(() => {
    const map = mapRef.current;
    const L = leafletRef.current;
    const group = layerGroupRef.current;
    if (!mapReady || !map || !L || !group) return;

    group.clearLayers();

    const originIcon = L.divIcon({
      className: "",
      html: '<div style="width:14px;height:14px;border-radius:9999px;background:oklch(0.676 0.175 253);box-shadow:0 0 0 4px oklch(0.676 0.175 253 / 0.25);border:2px solid white"></div>',
      iconSize: [14, 14],
      iconAnchor: [7, 7],
    });
    const destIcon = L.divIcon({
      className: "",
      html: '<div style="width:16px;height:16px;border-radius:9999px;background:oklch(0.60 0.135 155);box-shadow:0 0 0 4px oklch(0.60 0.135 155 / 0.25);border:2px solid white"></div>',
      iconSize: [16, 16],
      iconAnchor: [8, 8],
    });
    const currentIcon = L.divIcon({
      className: "",
      html: `<div style="width:30px;height:30px;border-radius:9999px;background:oklch(0.72 0.15 140);box-shadow:0 0 0 6px oklch(0.72 0.15 140 / 0.3);border:2px solid white;display:flex;align-items:center;justify-content:center">
        <svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">${vehicleIconPath(current?.vehicleType)}</svg>
      </div>`,
      iconSize: [30, 30],
      iconAnchor: [15, 15],
    });

    if (origin) L.marker([origin.lat, origin.lng], { icon: originIcon, zIndexOffset: 500 }).addTo(group).bindTooltip(origin.label ?? "Origin");
    if (destination) L.marker([destination.lat, destination.lng], { icon: destIcon, zIndexOffset: 500 }).addTo(group).bindTooltip(destination.label ?? "Destination");

    // Current-position marker lives outside the layer group and is moved
    // in place (setLatLng) rather than recreated — recreating it on every
    // ping is what used to force a view reset.
    if (current) {
      if (!currentMarkerRef.current) {
        currentMarkerRef.current = L.marker([current.lat, current.lng], { icon: currentIcon, zIndexOffset: 1000 })
          .addTo(map)
          .bindTooltip(current.label ?? "Live position");
      } else {
        currentMarkerRef.current.setLatLng([current.lat, current.lng]);
        currentMarkerRef.current.setIcon(currentIcon);
        currentMarkerRef.current.setTooltipContent(current.label ?? "Live position");
      }
    } else if (currentMarkerRef.current) {
      map.removeLayer(currentMarkerRef.current);
      currentMarkerRef.current = null;
    }

    if (origin && current && !hasHistory) {
      L.polyline([[origin.lat, origin.lng], [current.lat, current.lng]], {
        color: "#3D98FF",
        weight: 4,
        opacity: 0.85,
      }).addTo(group);
    }
    if (current && destination) {
      L.polyline([[current.lat, current.lng], [destination.lat, destination.lng]], {
        color: "#94a3b8",
        weight: 3,
        opacity: 0.6,
        dashArray: "6 8",
      }).addTo(group);
    }

    const historyBounds: [number, number][] = [];
    if (hasHistory && history && history.length >= 2) {
      const segments = classifySegments(history);
      const changeIcon = L.divIcon({
        className: "",
        html: '<div style="width:22px;height:22px;border-radius:9999px;background:#a855f7;box-shadow:0 0 0 4px rgba(168,85,247,0.25);border:2px solid white;display:flex;align-items:center;justify-content:center;color:white;font-weight:700;font-size:16px;line-height:1">&#8727;</div>',
        iconSize: [22, 22],
        iconAnchor: [11, 11],
      });
      const resumeIcon = L.divIcon({
        className: "",
        html: '<div style="width:22px;height:22px;border-radius:9999px;background:#3D98FF;box-shadow:0 0 0 4px rgba(61,152,255,0.25);border:2px solid white;display:flex;align-items:center;justify-content:center;color:white;font-weight:700;font-size:16px;line-height:1">&#8727;</div>',
        iconSize: [22, 22],
        iconAnchor: [11, 11],
      });

      for (let k = 0; k < history.length; k++) historyBounds.push([history[k].lat, history[k].lng]);

      for (let k = 0; k < history.length - 1; k++) {
        const a = history[k];
        const b = history[k + 1];
        const isStop = segments[k] === "stopped";
        L.polyline(
          [
            [a.lat, a.lng],
            [b.lat, b.lng],
          ],
          {
            color: isStop ? "#a855f7" : "#3D98FF",
            weight: 4,
            opacity: 0.85,
          },
        ).addTo(group);
      }

      // A marker at every color change — every flip into a stop/gap AND
      // every flip back to moving, however many times it happens on the
      // trip. Boundary run start/end used purely to label duration/kind.
      let prev: "moving" | "stopped" = "moving";
      for (let k = 0; k < segments.length; k++) {
        if (segments[k] === prev) continue;
        const at = history[k];
        if (segments[k] === "stopped") {
          let end = k;
          while (end + 1 < segments.length && segments[end + 1] === "stopped") end++;
          const last = history[end + 1];
          const durationMin = Math.round(
            (new Date(last.recorded_at).getTime() - new Date(at.recorded_at).getTime()) / 60000,
          );
          const hrs = Math.floor(durationMin / 60);
          const mins = durationMin % 60;
          const movedM = haversineM(at, last);
          const kind = movedM <= STOP_RADIUS_M ? "Stopped" : "Gap (no pings)";
          const label = `${kind} ${hrs > 0 ? `${hrs}h ` : ""}${mins}m — ${new Date(at.recorded_at).toLocaleTimeString()}`;
          L.marker([at.lat, at.lng], { icon: changeIcon }).addTo(group).bindTooltip(label);
        } else {
          const label = `Moving again — ${new Date(at.recorded_at).toLocaleTimeString()}`;
          L.marker([at.lat, at.lng], { icon: resumeIcon }).addTo(group).bindTooltip(label);
        }
        prev = segments[k];
      }
    }

    // Auto-fit happens exactly once, the first time the map has data to show
    // — never again after that, so a new ping can't reset the user's zoom/pan.
    if (!hasFitRef.current) {
      const bounds = [...points.map((p) => [p.lat, p.lng] as [number, number]), ...historyBounds];
      if (bounds.length === 1) {
        map.setView(bounds[0], 14);
        hasFitRef.current = true;
      } else if (bounds.length > 1) {
        map.fitBounds(bounds, { padding: [40, 40] });
        hasFitRef.current = true;
      }
    }

    checkOffScreen();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapReady, origin?.lat, origin?.lng, destination?.lat, destination?.lng, current?.lat, current?.lng, current?.vehicleType, current?.label, history]);

  if (!hasMap) {
    return (
      <div className={cn("relative flex flex-col items-center justify-center gap-2 overflow-hidden rounded-xl border bg-surface-muted p-6 text-center", className)}>
        <MapPin className="h-6 w-6 text-muted-foreground/50" />
        <p className="text-sm text-muted-foreground">Live map unavailable — no location data reported yet.</p>
      </div>
    );
  }

  return (
    <div className={cn("relative isolate overflow-hidden rounded-xl border bg-surface-muted", className)}>
      <div ref={containerRef} className="h-full min-h-64 w-full" />
      <div className="pointer-events-none absolute left-14 top-3 z-[400] flex items-center gap-2 rounded-full border bg-surface/90 px-2.5 py-1 text-xs font-medium shadow-sm backdrop-blur">
        <span className="h-2 w-2 rounded-full bg-success" />
        Live
      </div>
      <BasemapSwitcher value={basemap} onChange={setBasemap} />
      {hasHistory ? (
        <div className="pointer-events-none absolute bottom-3 left-3 z-[400] flex items-center gap-3 rounded-full border bg-surface/90 px-2.5 py-1 text-xs font-medium shadow-sm backdrop-blur">
          <span className="flex items-center gap-1.5"><span className="h-2 w-4 rounded-full bg-[#3D98FF]" /> Moving</span>
          <span className="flex items-center gap-1.5"><span className="h-2 w-4 rounded-full bg-[#a855f7]" /> Stopped / gap 30m+</span>
        </div>
      ) : null}
      {offScreen && current ? (
        <button
          type="button"
          onClick={goToCurrent}
          className="absolute right-3 top-1/2 z-[400] flex -translate-y-1/2 items-center gap-1.5 rounded-full border border-[#b8860b]/50 bg-[#d4af37] px-3 py-1.5 text-xs font-medium text-[#3d2f05] shadow-md backdrop-blur hover:bg-[#c9a227]"
        >
          <Crosshair className="h-3.5 w-3.5" /> Go to current location
        </button>
      ) : null}
      <PinContextMenu menu={pinMenu} onClose={() => setPinMenu(null)} onRemove={removeSearchPin} />
    </div>
  );
});
